import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  AlertCircle,
  FileText,
  Mail,
  Scale,
  Users
} from 'lucide-react';

const DigitalServicesActPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Scale className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Digital Services Act
              </h1>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed">
            RentSmart.ie is committed to full compliance with the Digital
            Services Act (DSA), the European Union's comprehensive framework for
            digital services. This page outlines our obligations and your rights
            under this regulation.
          </p>
        </div>

        {/* Service Provider Information */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Service Provider Information
            </h2>
          </div>

          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Legal Entity</h3>
              <p className="text-gray-700">RentSmart.ie</p>
              <p className="text-gray-600 text-sm">
                Operating as an online intermediary service for rental property
                listings in Ireland
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                Contact Information
              </h3>
              <p className="text-gray-700">
                Support:{' '}
                <a
                  href="mailto:support@rentsmart.ie"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@rentsmart.ie
                </a>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Service Type</h3>
              <p className="text-gray-700">
                Hosting Service / Online Platform (Intermediary Service under
                DSA Article 3(g))
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                Applicable Law
              </h3>
              <p className="text-gray-700">
                Republic of Ireland (EU Member State)
              </p>
              <p className="text-gray-600 text-sm mt-1">
                Subject to Irish law and EU regulations including Regulation
                (EU) 2022/2065 (Digital Services Act)
              </p>
            </div>
          </div>
        </div>

        {/* User Rights Under DSA */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Users className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Your Rights Under the DSA
            </h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
                  1
                </span>
                Right to Report Illegal Content
              </h3>
              <p className="text-gray-700 ml-8">
                You can report property listings or user content that you
                believe violates Irish or EU law, including fraudulent listings,
                discriminatory content, or violations of consumer protection
                laws.
              </p>
              <Link
                to="/report-listing"
                className="ml-8 inline-block mt-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                Report a Listing →
              </Link>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
                  2
                </span>
                Right to Contest Moderation Decisions
              </h3>
              <p className="text-gray-700 ml-8">
                If your listing is removed or your account is restricted, you
                have the right to request a review of this decision. We will
                respond within a reasonable timeframe with clear reasoning.
              </p>
              <a
                href="mailto:support@rentsmart.ie"
                className="ml-8 inline-block mt-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                Submit an Appeal →
              </a>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
                  3
                </span>
                Right to Access Out-of-Court Dispute Resolution
              </h3>
              <p className="text-gray-700 ml-8">
                You may access certified out-of-court dispute settlement bodies
                to resolve disputes related to content moderation decisions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
                  4
                </span>
                Right to Transparency
              </h3>
              <p className="text-gray-700 ml-8">
                We provide clear information about our content moderation
                practices, recommendation systems, and use of automated
                decision-making in our{' '}
                <Link
                  to="/community-guidelines"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Community Guidelines
                </Link>{' '}
                and{' '}
                <Link
                  to="/terms-of-service"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Terms of Service
                </Link>
                .
              </p>
            </div>
          </div>
        </div>

        {/* Content Moderation & Reporting */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Content Moderation & Reporting
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Our Commitment
              </h3>
              <p className="text-gray-700">
                RentSmart.ie operates a notice-and-action mechanism that enables
                users and trusted flaggers to report illegal content. We review
                all reports promptly and take appropriate action in accordance
                with DSA requirements.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What Can Be Reported
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Fraudulent or scam listings</li>
                <li>
                  Discriminatory content (violating EU Equality Directives)
                </li>
                <li>Misleading or false property information</li>
                <li>Violation of consumer protection laws</li>
                <li>Infringement of intellectual property rights</li>
                <li>Content promoting illegal activities</li>
                <li>Violations of data protection regulations (GDPR)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Reporting Process
              </h3>
              <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                <li>
                  Submit a detailed report via our{' '}
                  <Link
                    to="/report-listing"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    reporting form
                  </Link>
                </li>
                <li>
                  Our moderation team reviews the report within 48-72 hours
                </li>
                <li>
                  You receive a confirmation email with a reference number
                </li>
                <li>
                  We take action if content is found to violate our policies or
                  applicable law
                </li>
                <li>You are notified of the outcome and reasoning</li>
              </ol>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900">
                    Trusted Flagger Status
                  </h4>
                  <p className="text-amber-800 text-sm mt-1">
                    Entities with specialized expertise (e.g., consumer
                    protection authorities, anti-discrimination organizations)
                    can apply for trusted flagger status for priority review of
                    reports.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transparency & Accountability */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Transparency & Accountability
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Transparency Reports
              </h3>
              <p className="text-gray-700 mb-2">
                In compliance with DSA Article 15, we publish annual
                transparency reports containing:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>
                  Number of content removal orders received from authorities
                </li>
                <li>Number and categories of reports from users</li>
                <li>Actions taken on reported content</li>
                <li>Use of automated tools for content moderation</li>
                <li>Account suspensions and terminations</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Algorithmic Transparency
              </h3>
              <p className="text-gray-700">
                We use algorithms to rank property listings based on relevance,
                recency, and user preferences. Users can view and modify ranking
                parameters in their settings. We do not use automated profiling
                that may result in discriminatory outcomes.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Contact Point for Authorities
              </h3>
              <p className="text-gray-700">
                Designated contact for Irish and EU regulatory authorities:{' '}
                <a
                  href="mailto:support@rentsmart.ie"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@rentsmart.ie
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* User Safety & Protection */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              User Safety & Protection
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Know Your Customer (KYC)
              </h3>
              <p className="text-gray-700">
                Landlords and property agents may be required to verify their
                identity to list properties, helping prevent fraudulent listings
                and enhancing user trust.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Data Protection
              </h3>
              <p className="text-gray-700">
                We comply with GDPR (Regulation (EU) 2016/679) in addition to
                DSA requirements. See our{' '}
                <Link
                  to="/privacy-policy"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Privacy Policy
                </Link>{' '}
                for detailed information on data processing.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Safety Resources
              </h3>
              <p className="text-gray-700">
                Visit our{' '}
                <Link
                  to="/safety"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Safety Center
                </Link>{' '}
                for tips on avoiding scams and protecting your personal
                information when using our platform.
              </p>
            </div>
          </div>
        </div>

        {/* Contact & Support */}
        <div className="bg-blue-50 rounded-lg p-8">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Questions or Concerns?
              </h2>
              <p className="text-gray-700 mb-4">
                If you have questions about our DSA compliance or wish to
                exercise your rights under the Digital Services Act, please
                contact us:
              </p>
              <div className="space-y-2">
                <p className="text-gray-700">
                  <span className="font-semibold">General DSA Inquiries:</span>{' '}
                  <a
                    href="mailto:support@rentsmart.ie"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    support@rentsmart.ie
                  </a>
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold">Content Reporting:</span>{' '}
                  <Link
                    to="/report-listing"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Report a Listing
                  </Link>
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold">
                    Appeal Moderation Decisions:
                  </span>{' '}
                  <a
                    href="mailto:support@rentsmart.ie"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    support@rentsmart.ie
                  </a>
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold">Legal & Compliance:</span>{' '}
                  <a
                    href="mailto:support@rentsmart.ie"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    support@rentsmart.ie
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Related Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Link
              to="/terms-of-service"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              → Terms of Service
            </Link>
            <Link
              to="/privacy-policy"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              → Privacy Policy
            </Link>
            <Link
              to="/community-guidelines"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              → Community Guidelines
            </Link>
            <Link
              to="/report-listing"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              → Report a Listing
            </Link>
            <Link
              to="/safety"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              → Safety Center
            </Link>
            <Link
              to="/data-requests"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              → Data Requests
            </Link>
          </div>
        </div>

        {/* Last Updated */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Last updated: November 18, 2025
        </p>
      </div>
    </div>
  );
};

export default DigitalServicesActPage;
